import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Permission, userFitsSite } from '@construction/shared';
import { sitesApi, usersApi, type Site, type SiteMember } from '@/api/endpoints';
import {
  Alert,
  Badge,
  Button,
  Card,
  Drawer,
  EmptyState,
  Input,
  PageHeader,
  SettingsSection,
  Spinner,
} from '@/components/ui';
import { CheckList } from '@/components/CheckList';
import { parseSiteId, useCurrentSite } from '@/hooks/useCurrentSite';
import { usePermissions } from '@/lib/permissions';
import { SiteSettingsForm } from './SiteSettingsForm';
import { formatMoney } from '@/lib/format';
import { useErrorMessage } from '@/hooks/useErrorMessage';

/**
 * 工地设置（CR-082 按示例重做）：左边说明、右边表单的设置页 ——
 * 基本信息、打卡范围、考勤三段一个「保存更改」；现场人员、归档各自一张卡片，改了立刻生效。
 *
 * 改之前是只读卡片 +「编辑工地设置」切换成表单；网页端顶上还有一个「← 工地」（左边导航里就有「工地」，不再重复）。
 * 这个页面在两套外壳里都用，App 里的返回键由 MobileLayout 按路由表统一画（CR-044）。
 */
export function SiteDetailPage() {
  const { t } = useTranslation();
  // 手机端的管理者外壳里这个页面挂在 /m/settings/site，路由上没有工地段，
  // 落回当前选中的工地（useCurrentSite 在没有路由参数时就是这个行为）
  const { siteId: routeSiteId } = useParams();
  const { siteId: currentSiteId } = useCurrentSite();
  // 路由段是字符串，工地 id 是整数（CR-019）。转不出整数就当没有，
  // 落回手机端外壳记住的那个工地。
  const siteId = parseSiteId(routeSiteId) ?? currentSiteId;
  const toMessage = useErrorMessage();
  const { can } = usePermissions();
  // 问权限而不是比角色名：这里漏过 ADMIN，管理者改不了工地设置
  const canEdit = can(Permission.SITE_MANAGE);

  // siteId 为空时绝不能发请求：`GET /sites/` 会被路由成「工地列表」，
  // 于是 site.data 变成一个数组 —— 后面 s.lat.toFixed() 直接把页面打崩。
  // 手机端外壳里工地列表还没加载完就会短暂落到这个状态。
  const enabled = siteId !== null;
  const site = useQuery({
    queryKey: ['site', siteId],
    queryFn: () => sitesApi.get(siteId!),
    enabled,
  });

  if (!enabled || site.isLoading) {
    return (
      <div className="flex justify-center py-16 text-ink-3">
        <Spinner />
      </div>
    );
  }
  if (site.isError || !site.data) return <Alert>{toMessage(site.error)}</Alert>;

  const s = site.data;

  return (
    <>
      <PageHeader
        title={t('nav.siteSettings')}
        description={
          <span className="inline-flex flex-wrap items-center gap-2">
            {s.name}
            <Badge tone={s.status === 'ACTIVE' ? 'ok' : 'neutral'}>
              {s.status === 'ACTIVE' ? t('site.statusActive') : t('site.statusArchived')}
            </Badge>
          </span>
        }
      />

      {s.status === 'ARCHIVED' && (
        <div className="mb-4">
          <Alert tone="info">{t('site.archivedHint')}</Alert>
        </div>
      )}

      <div className="flex flex-col gap-4">
        {/* 换工地时整张表重来：表单里的值从新工地开始 */}
        <SiteSettingsForm key={s.id} site={s} readOnly={!canEdit} />
        <Members site={s} canEdit={canEdit} />
        {canEdit && <ArchiveCard site={s} />}
      </div>
    </>
  );
}

/** 现场人员：加人（勾选多人一次加，CR-071）、移出。改了立刻生效，不跟着上面的「保存更改」 */
function Members({ site, canEdit }: { site: Site; canEdit: boolean }) {
  const { t } = useTranslation();
  const toMessage = useErrorMessage();
  const [adding, setAdding] = useState(false);
  const [search, setSearch] = useState('');
  const members = useQuery({
    queryKey: ['site-members', site.id],
    queryFn: () => sitesApi.members(site.id),
  });

  const list = members.data ?? [];
  const needle = search.trim().toLowerCase();
  const shown = needle
    ? list.filter((m) => `${m.user.employeeNo} ${m.user.name}`.toLowerCase().includes(needle))
    : list;

  return (
    <Card>
      <SettingsSection
        title={t('site.members')}
        description={
          members.data ? t('site.membersHint', { count: members.data.length }) : undefined
        }
      >
        <div className="mb-3 flex flex-wrap items-center gap-2">
          {/* 人多了才用得上 */}
          {list.length > 8 && (
            <div className="min-w-48 max-w-xs flex-1 max-md:max-w-none">
              <Input
                type="search"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder={t('site.searchPeople')}
                aria-label={t('app.search')}
              />
            </div>
          )}
          {canEdit && (
            <div className="ml-auto">
              <Button variant="secondary" onClick={() => setAdding(true)} data-action="site.addMembers">
                {t('site.addMember')}
              </Button>
            </div>
          )}
        </div>

        {members.isLoading && (
          <div className="flex justify-center py-8 text-ink-3">
            <Spinner />
          </div>
        )}
        {members.isError && <Alert>{toMessage(members.error)}</Alert>}
        {members.data && list.length === 0 && <EmptyState title={t('site.noMembers')} />}
        {shown.length > 0 && (
          <ul className="divide-y divide-rule overflow-hidden rounded-lg border border-rule">
            {shown.map((m) => (
              <MemberRow key={m.id} siteId={site.id} member={m} currency={site.currency} canEdit={canEdit} />
            ))}
          </ul>
        )}
        {list.length > 0 && shown.length === 0 && <p className="text-body text-ink-3">{t('site.noMatches')}</p>}
      </SettingsSection>

      <Drawer open={adding} title={t('site.addMember')} subtitle={site.name} onClose={() => setAdding(false)}>
        <AddMembers siteId={site.id} siteOrgId={site.orgId} existing={list} onDone={() => setAdding(false)} />
      </Drawer>
    </Card>
  );
}

/** 一个人一行：工号、姓名、职位；右边时薪和「移出」 */
function MemberRow({
  siteId,
  member,
  currency,
  canEdit,
}: {
  siteId: number;
  member: SiteMember;
  currency: string;
  canEdit: boolean;
}) {
  const { t, i18n } = useTranslation();
  const queryClient = useQueryClient();
  const toMessage = useErrorMessage();
  const [error, setError] = useState<string | null>(null);

  const remove = useMutation({
    mutationFn: () => sitesApi.removeMember(siteId, member.user.id),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['site-members', siteId] });
      void queryClient.invalidateQueries({ queryKey: ['sites'] });
    },
    onError: (err) => setError(toMessage(err)),
  });

  return (
    <li className="flex min-h-row items-center gap-3 px-3 py-1.5">
      <span className="w-10 shrink-0 text-caption tabular-nums text-ink-3">{member.user.employeeNo}</span>
      <span className="min-w-0 flex-1">
        <span className="block truncate text-body font-medium text-ink">{member.user.name}</span>
        <span className="block truncate text-caption text-ink-3">
          {member.user.role.name}
          {member.user.hourlyRateCents != null &&
            ` · ${formatMoney(member.user.hourlyRateCents, i18n.language, currency)}`}
        </span>
        {error && <span className="block text-caption text-danger">{error}</span>}
      </span>
      {canEdit && (
        <Button
          size="sm"
          variant="ghost"
          loading={remove.isPending}
          onClick={() => {
            setError(null);
            if (window.confirm(t('site.removeMemberConfirm', { name: member.user.name }))) remove.mutate();
          }}
        >
          {t('site.removeMemberShort')}
        </Button>
      )}
    </li>
  );
}

/**
 * 加人：勾选多人一次加进来（CR-071，客户原话「工地设置里也支持多选增加用户」）。
 * 改之前是下拉选一个、点一下加一个，十个人要点十次。
 */
function AddMembers({
  siteId,
  siteOrgId,
  existing,
  onDone,
}: {
  siteId: number;
  siteOrgId: number;
  existing: SiteMember[];
  onDone: () => void;
}) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const toMessage = useErrorMessage();
  const [picked, setPicked] = useState<number[]>([]);
  const [error, setError] = useState<string | null>(null);

  // 在职账号一次拉全（接口上限 200 条；超过这个规模的公司再考虑分页选人）
  const allUsers = useQuery({
    queryKey: ['users', 'all'],
    queryFn: () => usersApi.list({ status: 'ACTIVE' as never, page: 1, pageSize: 200 }),
  });

  // 已经在这个工地的人不再出现，免得勾了才发现「已是成员」；
  // 别家的人也不出现（CR-073）—— 平台账号的用户列表里有所有家的人，勾了服务端回 ORG_MISMATCH
  const assigned = new Set(existing.map((m) => m.user.id));
  const candidates = (allUsers.data?.items ?? []).filter(
    (u) => !assigned.has(u.id) && userFitsSite(u.orgId, siteOrgId),
  );

  const add = useMutation({
    mutationFn: () => sitesApi.addMembers(siteId, picked),
    onSuccess: () => {
      setPicked([]);
      void queryClient.invalidateQueries({ queryKey: ['site-members', siteId] });
      void queryClient.invalidateQueries({ queryKey: ['sites'] });
      onDone();
    },
    onError: (err) => setError(toMessage(err)),
  });

  if (allUsers.isLoading) {
    return (
      <div className="flex justify-center py-10 text-ink-3">
        <Spinner />
      </div>
    );
  }
  if (allUsers.isError) return <Alert>{toMessage(allUsers.error)}</Alert>;
  if (candidates.length === 0) return <EmptyState title={t('site.noCandidates')} />;

  return (
    <div className="flex flex-col gap-4">
      {error && <Alert>{error}</Alert>}
      <p className="text-body text-ink-2">{t('site.pickPeople')}</p>
      <CheckList
        items={candidates.map((u) => ({
          id: u.id,
          label: `${u.employeeNo} · ${u.name}`,
          hint: u.role.name,
        }))}
        selected={picked}
        onChange={setPicked}
        labels={{
          search: t('site.searchPeople'),
          selectAll: t('site.selectAll'),
          clearAll: t('site.clearAll'),
          empty: t('site.noMatches'),
          picked: t('site.pickedCount', { count: picked.length }),
        }}
      />
      <div className="flex gap-2">
        <Button
          disabled={picked.length === 0}
          loading={add.isPending}
          onClick={() => {
            setError(null);
            add.mutate();
          }}
        >
          {t('site.addMembers', { count: picked.length })}
        </Button>
        <Button variant="secondary" onClick={onDone}>
          {t('app.cancel')}
        </Button>
      </div>
    </div>
  );
}

/**
 * 归档 / 恢复是单独的一张卡片 + 二次确认（CR-072），不在上面的表单里：
 * 下拉里一手滑就归档了，工地从所有人的切换器里消失，没人知道是谁什么时候点的
 */
function ArchiveCard({ site }: { site: Site }) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const toMessage = useErrorMessage();
  const [error, setError] = useState<string | null>(null);
  const archiving = site.status === 'ACTIVE';

  const setStatus = useMutation({
    mutationFn: (status: 'ACTIVE' | 'ARCHIVED') => sitesApi.update(site.id, { status }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['site', site.id] });
      void queryClient.invalidateQueries({ queryKey: ['sites'] });
    },
    onError: (err) => setError(toMessage(err)),
  });

  return (
    <Card>
      <SettingsSection
        title={archiving ? t('site.archiveTitle') : t('site.unarchive')}
        description={archiving ? t('site.archiveHint') : t('site.archivedHint')}
      >
        {error && (
          <div className="mb-3">
            <Alert>{error}</Alert>
          </div>
        )}
        <Button
          variant={archiving ? 'dangerOutline' : 'secondary'}
          loading={setStatus.isPending}
          data-action={archiving ? 'site.archive' : 'site.unarchive'}
          onClick={() => {
            setError(null);
            const ok = window.confirm(
              archiving
                ? t('site.archiveConfirm', { name: site.name })
                : t('site.unarchiveConfirm', { name: site.name }),
            );
            if (ok) setStatus.mutate(archiving ? 'ARCHIVED' : 'ACTIVE');
          }}
        >
          {archiving ? t('site.archive') : t('site.unarchive')}
        </Button>
      </SettingsSection>
    </Card>
  );
}
